import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { ReviewRequest, subscribeToStudentReviewRequests } from '../utils/reviewRequests';
import { getAllAuditEntriesForRabbi, AuditLogEntry } from '../utils/auditLog';
import { getVerseScoresByUser, VerseScoreDoc } from '../utils/verseScoresFirestore';
import { formatNumberForLocale, numberToHebrewLetters } from '../utils/hebrewNumbers';

const StudentDetailsPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he');

  const [studentInfo, setStudentInfo] = useState<{
    userId: string;
    userName: string;
    userEmail?: string;
    playCount: number;
    lastActivity?: Date;
  } | null>(null);
  const [recordings, setRecordings] = useState<ReviewRequest[]>([]);
  const [loadingRecordings, setLoadingRecordings] = useState(true);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [verseScores, setVerseScores] = useState<VerseScoreDoc[]>([]);
  const [loadingVerseScores, setLoadingVerseScores] = useState(true);
  const [playingRecordingId, setPlayingRecordingId] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  // Get rabbi info to filter audit entries
  const rabbiInfo = useMemo(() => {
    const stored = localStorage.getItem('rabbiInfo');
    if (!stored) return null;
    try {
      return JSON.parse(stored) as {
        id?: string;
        nameHe?: string;
        nameEn?: string;
      };
    } catch {
      return null;
    }
  }, []);

  // Load student info and audit entries
  useEffect(() => {
    if (!studentId || !rabbiInfo?.id) return;

    setLoadingAudit(true);
    getAllAuditEntriesForRabbi(rabbiInfo.id)
      .then((entries) => {
        // Filter entries for this student
        const studentEntries = entries.filter((entry) => entry.userId === studentId);

        setAuditEntries(studentEntries);

        // Extract student info from entries
        if (studentEntries.length > 0) {
          const firstEntry = studentEntries[0];
          let lastActivity: Date | undefined;
          if (firstEntry.createdAt) {
            // Firestore Timestamp has toDate() method
            const timestamp = firstEntry.createdAt as any;
            lastActivity =
              timestamp && typeof timestamp.toDate === 'function'
                ? timestamp.toDate()
                : new Date(timestamp);
          }

          setStudentInfo({
            userId: studentId,
            userName: firstEntry.userName || t('anonymous') || 'Anonymous',
            userEmail: firstEntry.userEmail,
            playCount: studentEntries.length,
            lastActivity,
          });
        }

        setLoadingAudit(false);
      })
      .catch((error) => {
        console.error('Error loading audit entries:', error);
        setLoadingAudit(false);
      });
  }, [studentId, rabbiInfo?.id, t]);

  // Load recordings
  useEffect(() => {
    if (!studentId) return;

    setLoadingRecordings(true);
    const unsubscribe = subscribeToStudentReviewRequests(studentId, (requests) => {
      // Filter by current rabbi
      const rabbiRequests = rabbiInfo?.id
        ? requests.filter((r) => r.rabbiId === rabbiInfo.id)
        : requests;
      setRecordings(rabbiRequests);
      setLoadingRecordings(false);
    });

    return () => unsubscribe();
  }, [studentId, rabbiInfo?.id]);

  // Load verse scores (psukim practiced + best score) from Firestore
  useEffect(() => {
    if (!studentId) return;
    setLoadingVerseScores(true);
    getVerseScoresByUser(studentId)
      .then((list) => {
        setVerseScores(list);
        setLoadingVerseScores(false);
      })
      .catch(() => setLoadingVerseScores(false));
  }, [studentId]);

  // Calculate practice time (estimate based on entries - assuming ~30 seconds per play)
  const practiceTimeMinutes = useMemo(() => {
    // Rough estimate: each play is about 30 seconds of practice
    const estimatedSeconds = auditEntries.length * 30;
    return Math.round(estimatedSeconds / 60);
  }, [auditEntries.length]);

  const handlePlayRecording = (request: ReviewRequest) => {
    if (!audioRef.current) return;

    if (playingRecordingId === request.id) {
      audioRef.current.pause();
      setPlayingRecordingId(null);
    } else {
      audioRef.current.src = request.audioUrl;
      audioRef.current.play();
      setPlayingRecordingId(request.id || null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!studentId) {
    return (
      <div
        className="h-full flex flex-col"
        style={{ background: 'var(--kol-white-soft, #F8F9FA)', color: 'var(--kol-navy, #132A49)' }}
      >
        <TopBar title={t('student_details') || 'Student Details'} onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center">
          <p>{t('student_not_found') || 'Student not found'}</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: 'var(--kol-white-soft, #F8F9FA)', color: 'var(--kol-navy, #132A49)' }}
    >
      <TopBar title={t('student_details') || 'Student Details'} onBack={() => navigate(-1)} />
      <div className="flex-1 px-5 pt-6 pb-24 space-y-6 overflow-y-auto">
        {/* Student Info Card */}
        {studentInfo && (
          <div
            dir={isHebrew ? 'rtl' : 'ltr'}
            className="bg-gradient-to-br from-white to-[#f9fafb] rounded-2xl shadow-md border border-[#e5e7eb] p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg"
                style={{ background: 'var(--kol-navy, #132A49)' }}
              >
                {studentInfo.userName[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#132a49]">{studentInfo.userName}</h2>
                {studentInfo.userEmail && (
                  <p className="text-sm text-gray-500">{studentInfo.userEmail}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div
                className="bg-white/80 rounded-xl p-3 text-center"
                style={{ border: '1px solid var(--kol-gray-200, #E4E7EB)' }}
              >
                <div className="text-2xl font-bold" style={{ color: 'var(--kol-navy, #132A49)' }}>
                  {studentInfo.playCount}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--kol-gray-500, #6B7C93)' }}>
                  {t('total_plays') || 'Total Plays'}
                </div>
              </div>
              <div
                className="bg-white/80 rounded-xl p-3 text-center"
                style={{ border: '1px solid var(--kol-gray-200, #E4E7EB)' }}
              >
                <div
                  className="text-2xl font-bold"
                  style={{ color: 'var(--kol-gold-primary, #C9A857)' }}
                >
                  {practiceTimeMinutes}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--kol-gray-500, #6B7C93)' }}>
                  {t('practice_minutes') || 'Practice Minutes'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Psukim practiced with best score */}
        <div dir={isHebrew ? 'rtl' : 'ltr'}>
          <h3 className="text-lg font-semibold mb-3">
            {t('psukim_practiced') || 'Psukim Practiced'} ({verseScores.length})
          </h3>
          {loadingVerseScores ? (
            <div
              className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-center"
              style={{
                border: '1px solid var(--kol-gray-200, #E4E7EB)',
                boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
              }}
            >
              <div
                className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
                style={{ borderColor: 'var(--kol-navy, #132A49)' }}
              ></div>
              <span className="ml-3 text-gray-500">{t('loading') || 'Loading...'}</span>
            </div>
          ) : verseScores.length > 0 ? (
            <div
              className="bg-white rounded-2xl shadow-md p-4"
              style={{
                border: '1px solid var(--kol-gray-200, #E4E7EB)',
                boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
              }}
            >
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {verseScores.map((vs) => (
                  <div
                    key={vs.verseKey}
                    className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-medium"
                        style={{ color: 'var(--kol-navy, #132A49)' }}
                      >
                        {vs.parasha}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('verse') || 'Verse'}{' '}
                        {formatNumberForLocale(vs.verseIndex + 1, i18n.language || '')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-500">
                        {t('best_score') || 'Best score'}:
                      </span>
                      <span
                        className="text-sm font-bold rounded-full px-2 py-0.5"
                        style={{
                          background: 'var(--kol-gold-cream, #F5EED6)',
                          color: 'var(--kol-gold-dark, #8B7532)',
                        }}
                      >
                        {vs.bestScore}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="bg-white rounded-2xl shadow-md p-6 text-center"
              style={{ border: '1px solid var(--kol-gray-200, #E4E7EB)' }}
            >
              <div className="text-4xl mb-2">📖</div>
              <h3
                className="text-lg font-semibold mb-1"
                style={{ color: 'var(--kol-navy, #132A49)' }}
              >
                {t('no_psukim_scores_yet') || 'No verse scores yet'}
              </h3>
              <p className="text-sm text-gray-500">
                {t('no_psukim_scores_desc') ||
                  'Best scores appear here after the student compares their reading in Practice.'}
              </p>
            </div>
          )}
        </div>

        {/* Recordings Section */}
        <div dir={isHebrew ? 'rtl' : 'ltr'}>
          <h3 className="text-lg font-semibold mb-3">
            {t('recordings') || 'Recordings'} ({recordings.length})
          </h3>
          {loadingRecordings ? (
            <div
              className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-center"
              style={{
                border: '1px solid var(--kol-gray-200, #E4E7EB)',
                boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
              }}
            >
              <div
                className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
                style={{ borderColor: 'var(--kol-navy, #132A49)' }}
              ></div>
              <span className="ml-3 text-gray-500">{t('loading') || 'Loading...'}</span>
            </div>
          ) : recordings.length > 0 ? (
            <div className="space-y-3">
              {recordings.map((recording) => (
                <div
                  key={recording.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-[#e5e7eb]"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-semibold text-[#132a49]">
                        {recording.parashaHebrew || recording.parashaKey}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('verse') || 'Verse'}{' '}
                        {formatNumberForLocale(recording.verseIndex + 1, i18n.language || '')} ·{' '}
                        {formatDate(recording.createdAt)}
                      </div>
                    </div>
                    <div
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        recording.status === 'approved'
                          ? 'bg-[#dcfce7] text-[#166534]'
                          : recording.status === 'rejected'
                            ? 'bg-[#fee2e2] text-[#991b1b]'
                            : recording.status === 'pending'
                              ? 'bg-[#fef3c7] text-[#854d0e]'
                              : 'bg-[#e0e7ff] text-[#3730a3]'
                      }`}
                    >
                      {recording.status === 'approved'
                        ? t('approved') || 'Approved'
                        : recording.status === 'rejected'
                          ? t('needs_work') || 'Needs Work'
                          : recording.status === 'pending'
                            ? t('pending') || 'Pending'
                            : t('reviewed') || 'Reviewed'}
                    </div>
                  </div>

                  {/* Verse text preview */}
                  <div
                    className="bg-[#f8f9fa] rounded-lg p-3 mb-3 text-sm text-[#132a49] leading-relaxed line-clamp-2"
                    dir="rtl"
                  >
                    {recording.verseText}
                  </div>

                  {/* Rabbi response if exists */}
                  {recording.rabbiResponse && (
                    <div className="bg-[#e0f2fe] rounded-lg p-3 mb-3 text-sm">
                      <div className="font-semibold text-[#0369a1] mb-1">
                        {t('rabbi_feedback') || 'Rabbi Feedback'}:
                      </div>
                      <div className="text-[#0c4a6e]">{recording.rabbiResponse}</div>
                    </div>
                  )}

                  {/* Play button */}
                  <button
                    onClick={() => handlePlayRecording(recording)}
                    className={`w-full py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                      playingRecordingId === recording.id ? 'text-white' : 'text-white border'
                    }`}
                    style={
                      playingRecordingId === recording.id
                        ? { background: 'var(--kol-navy, #132A49)' }
                        : {
                            background:
                              'var(--kol-gradient-gold, linear-gradient(90deg, #C9A857 0%, #B8924F 100%))',
                            color: 'var(--kol-navy, #132A49)',
                            border: 'none',
                            boxShadow: 'var(--kol-shadow-gold, 0 4px 16px rgba(201, 168, 87, 0.3))',
                          }
                    }
                  >
                    {playingRecordingId === recording.id ? '❚❚' : '▶'} {t('listen') || 'Listen'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-6 text-center">
              <div className="text-4xl mb-2">🎤</div>
              <h3 className="text-lg font-semibold text-[#132a49] mb-1">
                {t('no_recordings') || 'No Recordings'}
              </h3>
              <p className="text-sm text-gray-500">
                {t('no_recordings_desc') || "This student hasn't submitted any recordings yet."}
              </p>
            </div>
          )}
        </div>

        {/* Practice Activity Section */}
        <div dir={isHebrew ? 'rtl' : 'ltr'}>
          <h3 className="text-lg font-semibold mb-3">
            {t('practice_activity') || 'Practice Activity'}
          </h3>
          {loadingAudit ? (
            <div
              className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-center"
              style={{
                border: '1px solid var(--kol-gray-200, #E4E7EB)',
                boxShadow: 'var(--kol-shadow-md, 0 4px 12px rgba(10, 22, 40, 0.12))',
              }}
            >
              <div
                className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
                style={{ borderColor: 'var(--kol-navy, #132A49)' }}
              ></div>
              <span className="ml-3 text-gray-500">{t('loading') || 'Loading...'}</span>
            </div>
          ) : auditEntries.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {auditEntries.slice(0, 20).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                      style={{ background: 'var(--kol-navy, #132A49)' }}
                    >
                      ▶
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#132a49]">
                        {entry.parashaHebrew || entry.parashaKey}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('verse') || 'Verse'}{' '}
                        {formatNumberForLocale(entry.verseIndex + 1, i18n.language || '')} ·{' '}
                        {isHebrew
                          ? `${numberToHebrewLetters(entry.chapter)}:${numberToHebrewLetters(entry.pasuk)}`
                          : `${entry.chapter}:${entry.pasuk}`}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">
                      {formatDate(entry.createdAt)}
                    </div>
                  </div>
                ))}
                {auditEntries.length > 20 && (
                  <div className="text-center text-sm text-gray-500 pt-2">
                    {t('showing_recent_20') || 'Showing recent 20 activities'} (
                    {auditEntries.length} {t('total') || 'total'})
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-md border border-[#e5e7eb] p-6 text-center">
              <div className="text-4xl mb-2">📚</div>
              <h3 className="text-lg font-semibold text-[#132a49] mb-1">
                {t('no_practice_activity') || 'No Practice Activity'}
              </h3>
              <p className="text-sm text-gray-500">
                {t('no_practice_activity_desc') || "This student hasn't started practicing yet."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={() => setPlayingRecordingId(null)}
        onPause={() => setPlayingRecordingId(null)}
      />

      <BottomNav />
    </div>
  );
};

export default StudentDetailsPage;
